import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ArrowRight, User, ChevronDown, Menu, X } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { CONTACT } from "@/lib/siteData";
import { BrandLogo } from "@/components/BrandLogo";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { landingForUser } from "@/lib/roleRedirect";
import { usePublicPricing } from "@/lib/usePublicPricing";

/**
 * Responsive priority hierarchy (Feb 2026 audit):
 *   HIGH  — Logo · Book · Phone · Sign in        (always visible)
 *   MED   — Tests & Pricing · How It Works        (kept inline through tablet-landscape)
 *   LOW   — About · Knowledge Hub · Trade Portal  (collapse into "More" dropdown first)
 *
 * Breakpoints:
 *   ≥1280px (xl)            → full inline nav
 *   1024–1279px (lg only)   → 2 primary inline + "More ▼" dropdown
 *   <1024px (tablet/mobile) → hamburger drawer
 */
/**
 * Primary navigation — designed to surface the key SEO sections so Google
 * can render sitelinks under the brand search result.
 *
 *   Water Tests          → /  (dropdown with the 3 dedicated test pages)
 *   Service Areas        → /service-areas
 *   Learn                → /learn
 *   Contact              → /contact
 *
 * Property Managers is intentionally NOT in the main header nav — it's
 * surfaced via the footer, Knowledge Hub, rural/local pages and
 * property-management CTA sections. The /property-managers route + page
 * remain live for SEO + internal linking.
 *
 * Breakpoints:
 *   ≥1280px (xl)            → full inline nav
 *   1024–1279px (lg only)   → 1 primary inline + "More ▼" dropdown
 *   <1024px (tablet/mobile) → hamburger drawer
 */
const TEST_LINKS = [
  { label: "AWARE 4-Point", to: "/aware-4-point", testid: "nav-test-basic" },
  { label: "AWARE Tank 360", to: "/aware-tank-360", testid: "nav-test-tank" },
  { label: "AWARE Bore 360", to: "/aware-bore-360", testid: "nav-test-bore" },
];

const PRIMARY_NAV = [
  { label: "Popular Locations", to: "/locations", testid: "nav-service-areas" },
];
const SECONDARY_NAV = [
  { label: "Learn", to: "/learn", testid: "nav-learn", isLearn: true },
  { label: "My Water", to: "/my-water", testid: "nav-my-water" },
  { label: "Contact", to: "/contact", testid: "nav-contact" },
];

/**
 * "Learn" dropdown contents — Knowledge Hub is the primary destination
 * (existing /learn page); Blog is the secondary, kept under Learn so it
 * doesn't compete with the Book CTA for header attention.
 */
const LEARN_LINKS = [
  { label: "Knowledge Hub", to: "/learn", testid: "nav-learn-kh" },
  { label: "FAQs",          to: "/faqs", testid: "nav-learn-faqs" },
  { label: "Blog",          to: "/blog",  testid: "nav-learn-blog" },
];

/**
 * Homepage-only section nav (xl breakpoint only). Mobile drawer and the
 * lg "More" dropdown always use the site-wide nav for clarity. Hash links
 * use native <a> so the browser's smooth-scroll handles the in-page jump;
 * cross-page links (Passport, Locations) use <Link>.
 */
const HOMEPAGE_SECTION_NAV = [
  { label: "How It Works", kind: "hash",  href: "/#how-it-works", testid: "nav-home-how-it-works" },
  { label: "Reports",      kind: "hash",  href: "/#reports",      testid: "nav-home-reports" },
  { label: "Locations",    kind: "route", to: "/locations",       testid: "nav-home-locations" },
  { label: "Learn",        kind: "learn", testid: "nav-home-learn" },
  { label: "My Water",     kind: "route", to: "/my-water",        testid: "nav-home-my-water" },
  { label: "Contact",      kind: "route", to: "/contact",         testid: "nav-home-contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [testsOpen, setTestsOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const moreRef = useRef(null);
  const testsRef = useRef(null);
  const learnRef = useRef(null);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isHomepage = pathname === "/";
  const { enabled: discountEnabled, pct: discountPct } = usePublicPricing();
  // Feb 2026 — dynamic CTA label. When the online discount is disabled
  // the header falls back to a clean "Book online" so no "Save X%"
  // promo copy leaks through until the admin re-enables the switch.
  const bookCtaLabel = discountEnabled && discountPct > 0
    ? `Book & Save ${discountPct}%`
    : "Book online";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close "More" on outside click / Escape
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [moreOpen]);

  // Close "Water Tests" dropdown on outside click / Escape
  useEffect(() => {
    if (!testsOpen) return;
    const onClick = (e) => { if (testsRef.current && !testsRef.current.contains(e.target)) setTestsOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setTestsOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [testsOpen]);

  // Close "Learn" dropdown on outside click / Escape
  useEffect(() => {
    if (!learnOpen) return;
    const onClick = (e) => { if (learnRef.current && !learnRef.current.contains(e.target)) setLearnOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setLearnOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [learnOpen]);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const accountHref = user ? landingForUser(user) : "/login";

  return (
    <>
      <header
        data-testid={LANDING.header}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "py-2" : "py-4"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center gap-3 lg:gap-4 rounded-2xl transition-all duration-500 ${
              scrolled ? "glass px-4 py-2.5" : "glass-lite px-4 py-2.5"
            }`}
          >
            {/* Logo + brand transition tagline. Feb 2026 — a subtle
                "formerly Sunshine Coast Water Testing" sits under the
                AWARE Water Testing logo across desktop, tablet and
                mobile. Tagline reads from `BRAND.formerly` so it can
                be edited/removed in one place. Logo size unchanged. */}
            <Link to="/" data-testid={LANDING.headerLogo} className="flex items-center gap-2.5 group flex-shrink-0">
              <div className="flex flex-col items-start leading-none">
                <BrandLogo to={null} variant="plain" size="md" className="group-hover:scale-[1.02] transition-transform" />
                {BRAND.formerly && (
                  <span
                    data-testid="header-brand-formerly"
                    className="mt-1 text-[9px] sm:text-[10px] tracking-wide text-slate-500 whitespace-nowrap select-none"
                  >
                    {BRAND.formerly.charAt(0).toUpperCase() + BRAND.formerly.slice(1)}
                  </span>
                )}
              </div>
            </Link>

            {/* ============ DESKTOP NAV (≥1280px) ============ */}
            <nav className="hidden xl:flex items-center gap-0.5 ml-2" ref={testsRef}>
              {/* Water Tests dropdown */}
              <div className="relative">
                <button
                  data-testid="nav-water-tests-btn"
                  onClick={() => setTestsOpen((o) => !o)}
                  aria-expanded={testsOpen}
                  className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                >
                  Water Tests <ChevronDown className={`w-3.5 h-3.5 transition-transform ${testsOpen ? "rotate-180" : ""}`} />
                </button>
                {testsOpen && (
                  <div
                    data-testid="nav-water-tests-dropdown"
                    className="absolute left-0 top-full mt-2 w-72 rounded-2xl bg-white border border-slate-200 shadow-xl py-2 overflow-hidden"
                  >
                    {TEST_LINKS.map((t) => (
                      <Link
                        key={t.to}
                        to={t.to}
                        data-testid={t.testid}
                        onClick={() => setTestsOpen(false)}
                        className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                      >
                        {t.label}
                      </Link>
                    ))}
                    <div className="border-t border-slate-100 mt-1 pt-1">
                      <Link
                        to="/book-water-test"
                        data-testid="nav-water-tests-book"
                        onClick={() => setTestsOpen(false)}
                        className="block px-4 py-2.5 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 transition-colors"
                      >
                        Book a Water Test →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {isHomepage ? (
                // ── Homepage-only section nav (xl only, per UX spec) ──
                HOMEPAGE_SECTION_NAV.map((n) => {
                  if (n.kind === "hash") {
                    return (
                      <a
                        key={n.href}
                        href={n.href}
                        data-testid={n.testid}
                        className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors whitespace-nowrap"
                      >
                        {n.label}
                      </a>
                    );
                  }
                  if (n.kind === "learn") {
                    return (
                      <div key="learn-home" className="relative" ref={learnRef}>
                        <button
                          data-testid={n.testid}
                          onClick={() => { setLearnOpen((o) => !o); setTestsOpen(false); }}
                          aria-expanded={learnOpen}
                          className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                        >
                          Learn <ChevronDown className={`w-3.5 h-3.5 transition-transform ${learnOpen ? "rotate-180" : ""}`} />
                        </button>
                        {learnOpen && (
                          <div
                            data-testid="nav-learn-dropdown"
                            className="absolute left-0 top-full mt-2 w-56 rounded-2xl bg-white border border-slate-200 shadow-xl py-2 overflow-hidden"
                          >
                            {LEARN_LINKS.map((l) => (
                              <Link
                                key={l.to}
                                to={l.to}
                                data-testid={l.testid}
                                onClick={() => setLearnOpen(false)}
                                className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                              >
                                {l.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={n.testid}
                      className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors whitespace-nowrap"
                    >
                      {n.label}
                    </Link>
                  );
                })
              ) : (
                <>
                  {PRIMARY_NAV.map((n) => (
                    <Link
                      key={n.to} to={n.to} data-testid={n.testid}
                      className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors whitespace-nowrap"
                    >
                      {n.label}
                    </Link>
                  ))}
                  {SECONDARY_NAV.map((n) => (
                    n.isLearn ? (
                      <div key="learn-inner" className="relative" ref={learnRef}>
                        <button
                          data-testid={n.testid}
                          onClick={() => { setLearnOpen((o) => !o); setTestsOpen(false); }}
                          aria-expanded={learnOpen}
                          className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                        >
                          Learn <ChevronDown className={`w-3.5 h-3.5 transition-transform ${learnOpen ? "rotate-180" : ""}`} />
                        </button>
                        {learnOpen && (
                          <div
                            data-testid="nav-learn-dropdown"
                            className="absolute left-0 top-full mt-2 w-56 rounded-2xl bg-white border border-slate-200 shadow-xl py-2 overflow-hidden"
                          >
                            {LEARN_LINKS.map((l) => (
                              <Link
                                key={l.to}
                                to={l.to}
                                data-testid={l.testid}
                                onClick={() => setLearnOpen(false)}
                                className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                              >
                                {l.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={n.to} to={n.to} data-testid={n.testid}
                        className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors whitespace-nowrap"
                      >
                        {n.label}
                      </Link>
                    )
                  ))}
                </>
              )}
            </nav>

            {/* ============ TABLET-LANDSCAPE NAV (1024–1279px): Water Tests + 1 primary + More ============ */}
            <nav className="hidden lg:flex xl:hidden items-center gap-0.5 ml-2" ref={moreRef}>
              {/* Water Tests dropdown (reused state) */}
              <div className="relative">
                <button
                  data-testid="nav-water-tests-btn-tablet"
                  onClick={() => setTestsOpen((o) => !o)}
                  aria-expanded={testsOpen}
                  className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                >
                  Water Tests <ChevronDown className={`w-3.5 h-3.5 transition-transform ${testsOpen ? "rotate-180" : ""}`} />
                </button>
                {testsOpen && (
                  <div className="absolute left-0 top-full mt-2 w-72 rounded-2xl bg-white border border-slate-200 shadow-xl py-2 overflow-hidden">
                    {TEST_LINKS.map((t) => (
                      <Link
                        key={t.to} to={t.to}
                        data-testid={`${t.testid}-tablet`}
                        onClick={() => setTestsOpen(false)}
                        className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                      >
                        {t.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  data-testid="nav-more-btn"
                  onClick={() => setMoreOpen((o) => !o)}
                  aria-expanded={moreOpen}
                  className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                >
                  More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                </button>
                {moreOpen && (
                  <div
                    data-testid="nav-more-dropdown"
                    className="absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white border border-slate-200 shadow-xl py-2 overflow-hidden"
                  >
                    <Link to="/locations" data-testid="nav-service-areas-more" onClick={() => setMoreOpen(false)} className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors">Popular Locations</Link>
                    {SECONDARY_NAV.map((n) => (
                      n.isLearn ? (
                        <div key="learn-more">
                          <Link
                            to="/knowledge-hub"
                            data-testid="nav-learn-more"
                            onClick={() => setMoreOpen(false)}
                            className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                          >
                            Knowledge Hub
                          </Link>
                          <Link
                            to="/blog"
                            data-testid="nav-blog-more"
                            onClick={() => setMoreOpen(false)}
                            className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                          >
                            Blog
                          </Link>
                        </div>
                      ) : (
                        <Link
                          key={n.to} to={n.to} data-testid={`${n.testid}-more`}
                          onClick={() => setMoreOpen(false)}
                          className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-700 transition-colors"
                        >
                          {n.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* ============ RIGHT-SIDE CTAs (always visible, never wrap) ============ */}
            <div className="flex items-center gap-1.5 sm:gap-2 ml-auto flex-shrink-0">
              {/* Phone — icon always, number ≥xl, abbreviated label between */}
              <a
                href={CONTACT.phoneLink}
                data-testid={LANDING.headerCallBtn}
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-semibold text-slate-900 hover:text-cyan-600 transition-colors whitespace-nowrap rounded-lg hover:bg-slate-100/70"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span className="hidden xl:inline tabular-nums">{CONTACT.phone}</span>
                <span className="hidden lg:inline xl:hidden">Call us</span>
              </a>

              {/* Sign in / Account — icon-only on tablet, full label on desktop */}
              <Link
                to={accountHref}
                data-testid="header-account-link"
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-semibold text-slate-700 hover:text-cyan-600 transition-colors whitespace-nowrap rounded-lg hover:bg-slate-100/70"
              >
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="hidden xl:inline">{user ? "My account" : "Sign in"}</span>
              </Link>

              {/* Book — most prominent, always visible */}
              <Link
                to="/book-water-test"
                data-testid={LANDING.headerBookBtn}
                className="btn-aqua group inline-flex items-center gap-1.5 px-3.5 sm:px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0"
              >
                <span className="hidden sm:inline">{bookCtaLabel}</span>
                <span className="sm:hidden">Book</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              {/* Hamburger — only below lg */}
              <button
                data-testid="nav-hamburger"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-100/70 transition-colors flex-shrink-0"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ============ MOBILE / TABLET-PORTRAIT DRAWER ============ */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="nav-drawer">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[88vw] bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex flex-col items-start leading-none">
                <BrandLogo to="/" size="sm" />
                {BRAND.formerly && (
                  <span
                    data-testid="drawer-brand-formerly"
                    className="mt-1 text-[10px] tracking-wide text-slate-500 whitespace-nowrap select-none"
                  >
                    {BRAND.formerly.charAt(0).toUpperCase() + BRAND.formerly.slice(1)}
                  </span>
                )}
              </div>
              <button
                data-testid="nav-drawer-close"
                onClick={() => setDrawerOpen(false)}
                className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              <Link
                to="/"
                data-testid="nav-home-drawer"
                onClick={() => setDrawerOpen(false)}
                className="block px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
              >
                Home
              </Link>
              <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">
                Water Tests
              </div>
              {TEST_LINKS.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`${t.testid}-drawer`}
                  onClick={() => setDrawerOpen(false)}
                  className="block px-3 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-slate-100"
                >
                  {t.label}
                </Link>
              ))}
              <div className="my-3 h-px bg-slate-100" />
              {PRIMARY_NAV.map((n) => (
                <Link
                  key={n.to} to={n.to}
                  data-testid={`${n.testid}-drawer`}
                  onClick={() => setDrawerOpen(false)}
                  className="block px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
                >
                  {n.label}
                </Link>
              ))}
              {SECONDARY_NAV.map((n) => (
                n.isLearn ? (
                  <div key="learn-drawer">
                    <Link
                      to="/knowledge-hub"
                      data-testid="nav-learn-drawer"
                      onClick={() => setDrawerOpen(false)}
                      className="block px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
                    >
                      Knowledge Hub
                    </Link>
                    <Link
                      to="/blog"
                      data-testid="nav-blog-drawer"
                      onClick={() => setDrawerOpen(false)}
                      className="block px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
                    >
                      Blog
                    </Link>
                  </div>
                ) : (
                  <Link
                    key={n.to} to={n.to} data-testid={`${n.testid}-drawer`}
                    onClick={() => setDrawerOpen(false)}
                    className="block px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
                  >
                    {n.label}
                  </Link>
                )
              ))}
              <div className="my-3 h-px bg-slate-100" />
              <Link
                to={accountHref}
                data-testid="nav-drawer-account"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
              >
                <User className="w-4 h-4" /> {user ? "My account" : "Sign in"}
              </Link>
              <a
                href={CONTACT.phoneLink}
                data-testid="nav-drawer-call"
                className="flex items-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold text-slate-800 hover:bg-slate-100"
              >
                <Phone className="w-4 h-4" /> <span className="tabular-nums">{CONTACT.phone}</span>
              </a>
            </nav>
            <div className="px-5 pb-5 pt-3 border-t border-slate-100">
              <Link
                to="/book-water-test"
                data-testid="nav-drawer-book"
                onClick={() => setDrawerOpen(false)}
                className="btn-aqua w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-sm font-semibold"
              >
                {bookCtaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
